<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { LuxonDateTimeFormats, formatDateTimeWithMilitarySupport } from '@nova/core';

import LoadTypeSelectV2 from '@/components/elements/selects/LoadTypeSelectV2.vue';
import { useOrg, useSelectedWarehouseSettings } from '@/composables';
import RescheduleFromToSummary from '@/modules/appointments/components/reschedule/RescheduleFromToSummary.vue';

import type { DockDetails } from '@satellite/composables/dockPresenterShared';
import type { LoadTypeDetails } from '@satellite/composables/loadTypePresenterShared';
import type { WarehouseDetails } from '@satellite/composables/useWarehousesPresenter';

/** Either a real appointment or a calendar event turned plain object; both carry the pair. */
interface ReselectEvent {
  start?: string;
  end?: string;
}

const {
  event,
  eventWarehouse,
  eventDock,
  originalEvent,
  originalEventDock,
  showDialog = false
} = defineProps<{
  /** Where the appointment is being moved to. */
  event: ReselectEvent;
  eventWarehouse: WarehouseDetails;
  eventDock?: DockDetails;
  /** Where it sits today. */
  originalEvent: ReselectEvent;
  originalEventDock: DockDetails;
  showDialog?: boolean;
}>();

const emit = defineEmits<{
  confirm: [loadType: LoadTypeDetails];
  close: [];
  cancel: [];
}>();

const { t, locale } = useI18n();
const { orgRef } = useOrg();
/**
 * The legacy reschedule passes `appointment.dock.warehouse`, a store join without `org`, so the
 * global org stands in there; the calendar and V2 paths pass a warehouse that carries its own.
 */
const { isMilitaryTimeEnabled } = useSelectedWarehouseSettings(() => eventWarehouse, {
  org: () => eventWarehouse.org ?? orgRef.value,
  warnOnMissingOrg: true
});

const newLoadType = ref<LoadTypeDetails | null>(null);

/** Nothing should carry over from a previous open. */
watch(
  () => showDialog,
  isOpen => {
    if (!isOpen) {
      newLoadType.value = null;
    }
  }
);

function formatDate(iso?: string) {
  if (!iso) {
    return '';
  }

  return formatDateTimeWithMilitarySupport(
    iso,
    eventWarehouse.timezone,
    LuxonDateTimeFormats.LongDateShortMonth,
    isMilitaryTimeEnabled(),
    LuxonDateTimeFormats.LongDateShortMonth,
    locale.value
  );
}

function formatTime(iso?: string) {
  if (!iso) {
    return '';
  }

  return formatDateTimeWithMilitarySupport(
    iso,
    eventWarehouse.timezone,
    LuxonDateTimeFormats.Extended12HrTimeAMPM,
    isMilitaryTimeEnabled(),
    LuxonDateTimeFormats.Extended24HrTime,
    locale.value
  );
}

function buildSummarySide(slot: ReselectEvent, dockName: string) {
  return {
    date: formatDate(slot.start),
    timeRange: `${formatTime(slot.start)} - ${formatTime(slot.end)}`,
    dock: dockName
  };
}

const rescheduleSummary = computed(() => ({
  from: buildSummarySide(originalEvent, originalEventDock?.name ?? ''),
  /**
   * Both sides read their date from `start`. This one used to read `end`, which showed
   * tomorrow for an appointment running past midnight — the time range beside it was
   * already built from the correct pair, so only the date was ever wrong.
   */
  to: buildSummarySide(event, eventDock?.name ?? '')
}));

/** The dock being moved to is what decides which load types are on offer. */
const narrowingDocks = computed(() => (eventDock ? [eventDock] : []));

function handleCancel() {
  emit('cancel');
  emit('close');
}

function submitLoadType() {
  if (!newLoadType.value) {
    return;
  }

  emit('confirm', newLoadType.value);
  newLoadType.value = null;
  emit('close');
}
</script>

<template>
  <!--
    Persistent like the legacy dialog, so a stray click outside does not undo the dock change;
    Escape still cancels, like Nevermind.
  -->
  <v-dialog
    persistent
    scrollable
    max-width="500px"
    width="100%"
    content-class="load-type-reselect-dialog"
    :model-value="showDialog"
    @keydown.escape="handleCancel">
    <v-card data-testid="load-type-reselect-dialog">
      <v-card-title class="d-flex align-center ga-2 px-5 py-4 border-b">
        <v-icon color="-color-warning-100" icon="mdi-alert" size="16" />
        <h2 class="typography-heading-md-bold ma-0">
          {{ t('appointment.dialogs.loadTypeReselectDialog.headers.loadTypeNotSupported') }}
        </h2>
      </v-card-title>

      <v-card-text class="d-flex flex-column ga-5 pa-5">
        <p class="typography-body-md ma-0">
          <span class="typography-body-md-bold">
            {{
              t(
                'appointment.dialogs.loadTypeReselectDialog.messages.thisAppointmentsLoadTypeIsNotCurrentlySupportedAtThisDock'
              )
            }}.
          </span>
          {{
            t(
              'appointment.dialogs.loadTypeReselectDialog.messages.selectANewLoadTypeOrRescheduleAtADifferentDock'
            )
          }}
        </p>

        <load-type-select-v2
          v-model="newLoadType"
          show-only-assigned-load-types
          data-testid="load-type-reselect-dialog-select"
          :docks="narrowingDocks"
          :warehouse="eventWarehouse" />

        <reschedule-from-to-summary :from="rescheduleSummary.from" :to="rescheduleSummary.to" />
      </v-card-text>

      <v-divider />

      <v-card-actions class="d-flex justify-end ga-2 px-5 py-4">
        <v-btn variant="outlined" @click="handleCancel">
          {{ t('global.buttons.nevermind') }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          data-testid="load-type-reselect-dialog-confirm"
          :disabled="!newLoadType"
          @click="submitLoadType">
          {{ t('global.basicTerms.reschedule') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
